import { useCallback, useEffect, useState } from 'react';
import {
  useParams,
  useLocation,
  useNavigate,
  generatePath,
  useMatch,
} from 'react-router-dom';
import {
  ExchangeSidepanel,
  ExchangeTransaction,
  PermissionEnum,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import { clearTransactionState } from '../store/slices/exchange-transaction';
import {
  useLazyGetCustomersTransactionLogsQuery,
  useLazyGetCustomerExchangeTransactionQuery,
  useLazyGetProblemTransactionChatsQuery,
} from '../store/services/customers';

const ProblemExchangeTransactionHOC = ({
  backPath,
  generalPath,
  logPath,
  matchPath,
  chatPath,
  changeLogPath,
  commentsPath,
}: {
  backPath: string;
  generalPath: string;
  logPath: string;
  matchPath: string;
  chatPath: string;
  changeLogPath: string;
  commentsPath: string;
}): React.JSX.Element => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const {
    customersProblemTransactions: { exchange },
    customersDictionaries: {
      activePermissions,
      currenciesList,
      overdraftEnabled,
    },
  } = useAppSelector((state) => state);

  const { customerId, transactionId } = useParams();

  const [getExcahngeLogs, { isLoading: logsIsLoading }] =
    useLazyGetCustomersTransactionLogsQuery();

  const [getCustomerExchangeTransaction, { isLoading, isFetching }] =
    useLazyGetCustomerExchangeTransactionQuery();

  const [getChats] = useLazyGetProblemTransactionChatsQuery();

  const getExchangeTransactionById = useCallback(() => {
    getCustomerExchangeTransaction(transactionId as string);

    if (
      activePermissions?.includes(
        PermissionEnum.TRANSACTION_QUEUE_ITEMS_EXCHANGE_LIST,
      )
    ) {
      getExcahngeLogs(transactionId as string);
    }

    if (activePermissions?.includes(PermissionEnum.CHANNELS_LIST)) {
      getChats(transactionId as string);
    }
  }, [transactionId, activePermissions]);

  const { search } = useLocation();
  const navigate = useNavigate();
  const match = useMatch(matchPath);

  const dispatch = useAppDispatch();

  const handleClose = () => {
    setPanelIsOpen(false);

    setTimeout(() => {
      navigate(
        generatePath(`${backPath}${search || ''}`, {
          customerId,
        }),
      );
    }, 100);
  };

  useEffect(() => {
    setPanelIsOpen(true);

    return () => {
      dispatch(clearTransactionState());
    };
  }, [dispatch]);

  useEffect(() => {
    if (customerId && !exchange?.transaction && transactionId) {
      getExchangeTransactionById();
    }
  }, [
    getExchangeTransactionById,
    customerId,
    exchange?.transaction,
    transactionId,
  ]);

  useEffect(() => {
    if (match && customerId && transactionId) {
      navigate(
        generatePath(`${generalPath}${search || ''}`, {
          customerId,
          transactionId,
        }),
      );
    }
  }, [match, customerId, transactionId]);

  return (
    <ExchangeSidepanel
      panelIsOpen={panelIsOpen}
      handleClose={handleClose}
      tabs={{
        generalPath: generatePath(`${generalPath}${search || ''}`, {
          customerId,
          transactionId,
        }),
        logPath: generatePath(`${logPath}${search || ''}`, {
          customerId,
          transactionId,
        }),
        changeLogPath: generatePath(`${changeLogPath}${search || ''}`, {
          customerId,
          transactionId,
        }),
        chatPath: generatePath(`${chatPath}${search || ''}`, {
          customerId,
          transactionId,
        }),
        commentsPath: generatePath(`${commentsPath}${search || ''}`, {
          customerId,
          transactionId,
        }),
      }}
      isLoading={logsIsLoading || isLoading || isFetching}
      title="Exchange Transaction"
      currenciesList={currenciesList}
      transactionInfo={exchange as ExchangeTransaction}
      overdraftEnabled={overdraftEnabled}
      sidepanelExpandedKey="problem-exchange"
    />
  );
};

export default ProblemExchangeTransactionHOC;
